import { PRICE_SOURCE_LABELS, QUOTE_STATUS_LABELS, formatDate, formatDateTime, formatILS, renderTemplate, firstNameOf, whatsappLink, type QuoteStatus } from '@takumi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Check, Copy, ExternalLink, FileText, MessageCircle, Printer, Send, ShoppingBag, X } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge, Card, CardHeader, EmptyState, PageLoader } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useOrg } from '@/lib/queries';
import { SendMessageDialog } from '@/features/messaging/SendMessageDialog';
import { useState } from 'react';
import { Mail } from 'lucide-react';
import { useInvalidateCrm } from '@/lib/queries';
import type { Quote } from '@/lib/sales-types';

export const QUOTE_STATUS_VARIANT: Record<QuoteStatus, 'neutral' | 'tomorrow' | 'today' | 'matcha' | 'overdue' | 'outline'> = {
  draft: 'neutral', sent: 'tomorrow', viewed: 'today', accepted: 'matcha', rejected: 'overdue', expired: 'outline',
};

export function QuotePage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invalidate = useInvalidateCrm();
  const { data: q, isLoading } = useQuery({ queryKey: ['quote', id], queryFn: () => api.get<Quote>(`/quotes/${id}`) });
  const org = useOrg(q?.organizationId ?? '');
  const [emailing, setEmailing] = useState(false);

  const act = useMutation({
    mutationFn: (action: 'send' | 'accept' | 'reject' | 'convert') => api.post<{ id: string }>(`/quotes/${id}/${action}`, {}),
    onSuccess: (res, action) => {
      void qc.invalidateQueries({ queryKey: ['quote', id] });
      invalidate(q?.organizationId);
      if (action === 'convert') {
        toast.success('נוצרה הזמנה מההצעה');
        navigate(`/orders/${res.id}`);
      } else toast.success({ send: 'ההצעה סומנה כנשלחה ונקבע פולואפ', accept: 'ההצעה אושרה', reject: 'ההצעה סומנה כנדחתה' }[action]);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (isLoading) return <PageLoader />;
  if (!q) return <EmptyState title="הצעת המחיר לא נמצאה" />;

  const publicUrl = `${window.location.origin}/q/${q.publicToken}`;
  const waText = renderTemplate('שלום {{first_name}}, מצורפת הצעת המחיר של Takumi Matcha: {{link}}', { first_name: firstNameOf(q.contactName) || '', link: publicUrl });
  const wa = whatsappLink(q.contactWhatsapp, waText);
  const open = ['draft', 'sent', 'viewed'].includes(q.status);

  return (
    <div className="mx-auto max-w-5xl">
      <Link to={`/org/${q.organizationId}`} className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink print:hidden">
        <ArrowRight className="size-4" /> {q.organizationName}
      </Link>
      <div className="mb-5 flex flex-wrap items-center gap-3 print:hidden">
        <h1 className="flex items-center gap-2 text-[26px] font-semibold">
          <FileText className="size-6 text-gold-500" /> הצעת מחיר <span className="ltr">{q.number}</span>
        </h1>
        <Badge variant={QUOTE_STATUS_VARIANT[q.status]}>{QUOTE_STATUS_LABELS[q.status]}</Badge>
        <div className="ms-auto flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => window.print()}><Printer /> הדפסה / PDF</Button>
          {can('crm.write') && open && <Button variant="secondary" onClick={() => setEmailing(true)}><Mail /> שליחה באימייל</Button>}
          {can('sales.write') && q.status === 'draft' && <Button onClick={() => act.mutate('send')} loading={act.isPending}><Send /> סימון כנשלחה</Button>}
          {can('sales.write') && open && q.status !== 'draft' && (
            <>
              <Button variant="secondary" onClick={() => act.mutate('reject')}><X /> נדחתה</Button>
              <Button variant="secondary" onClick={() => act.mutate('accept')}><Check /> אושרה</Button>
            </>
          )}
          {can('sales.write') && !q.orderId && q.status !== 'rejected' && q.status !== 'expired' && (
            <Button variant="gold" onClick={() => act.mutate('convert')} loading={act.isPending}><ShoppingBag /> המרה להזמנה</Button>
          )}
          {q.orderId && <Button asChild variant="secondary"><Link to={`/orders/${q.orderId}`}><ShoppingBag /> להזמנה</Link></Button>}
        </div>
      </div>

      {q.status !== 'draft' && (
        <Card className="mb-5 flex flex-wrap items-center gap-3 p-4 print:hidden">
          <div className="min-w-0 flex-1 text-sm">
            <div className="font-semibold">קישור ללקוח</div>
            <div className="ltr truncate text-muted">{publicUrl}</div>
            <div className="mt-1 text-xs text-muted">
              {q.sentAt && `נשלחה ${formatDateTime(q.sentAt)}`}{q.viewedAt && ` · נצפתה ${formatDateTime(q.viewedAt)}`}
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={() => void navigator.clipboard.writeText(publicUrl).then(() => toast.success('הקישור הועתק'))}><Copy /> העתקה</Button>
          <Button variant="secondary" size="sm" asChild><a href={publicUrl} target="_blank" rel="noopener noreferrer"><ExternalLink /> תצוגת לקוח</a></Button>
          {wa && <Button variant="secondary" size="sm" asChild><a href={wa} target="_blank" rel="noopener noreferrer"><MessageCircle /> שליחה ב-WhatsApp</a></Button>}
        </Card>
      )}
      {q.status === 'draft' && (
        <p className="mb-5 rounded-card border border-line bg-sunken/60 p-3 text-sm text-ink-soft print:hidden">
          הצעה בטיוטה. לאחר "סימון כנשלחה" יופק קישור ללקוח וייקבע פולואפ אוטומטי. שליחה ישירה באימייל תתאפשר עם חיבור האימייל (שלב 5).
        </p>
      )}

      {emailing && org.data && (
        <SendMessageDialog
          channel="email"
          open
          onOpenChange={(o) => {
            setEmailing(o);
            if (!o) void qc.invalidateQueries({ queryKey: ['quote', id] });
          }}
          organizationId={q.organizationId}
          organizationName={q.organizationName}
          contacts={org.data.contacts}
          quoteId={q.id}
          defaultTemplateKey="quote"
        />
      )}
      <Card className="p-8 print:border-0 print:p-0 print:shadow-none">
        <div className="flex flex-wrap justify-between gap-4 border-b-2 border-matcha-700 pb-4">
          <div>
            <div className="font-display text-2xl font-semibold tracking-wide text-matcha-800" dir="ltr">TAKUMI MATCHA</div>
            <div className="text-sm text-muted">מאצ׳ה טקסית איכותית וכשרה לעסקים</div>
          </div>
          <div className="text-sm text-muted">
            <div className="text-lg font-semibold text-ink">הצעת מחיר <span className="ltr">{q.number}</span></div>
            <div>תאריך: {formatDate(q.issueDate)}</div>
            {q.validUntil && <div>בתוקף עד: {formatDate(q.validUntil)}</div>}
          </div>
        </div>
        <div className="my-5 text-sm">
          <div className="text-xs font-bold text-muted">לכבוד</div>
          <div className="font-semibold">{q.organizationName}</div>
          {q.contactName && <div>לידי {q.contactName}</div>}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-sunken text-xs font-bold text-muted">
            <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-start">
              <th>#</th><th>פריט</th><th className="text-end">כמות</th><th className="text-end">מחיר ליחידה</th><th className="text-end">הנחה</th><th className="text-end">סה״כ</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {q.items.map((i) => (
              <tr key={i.id} className="[&>td]:px-3 [&>td]:py-2.5">
                <td>{i.position}</td>
                <td>
                  {i.description}
                  <div className="text-[11px] text-muted print:hidden">{PRICE_SOURCE_LABELS[i.priceSource]}{i.overrideReason ? ` · ${i.overrideReason}` : ''}</div>
                </td>
                <td className="text-end tabular">{i.quantity}</td>
                <td className="text-end tabular">{formatILS(i.unitPrice)}</td>
                <td className="text-end tabular">{i.discountPct ? `${i.discountPct}%` : '—'}</td>
                <td className="text-end font-semibold tabular">{formatILS(i.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <dl className="ms-auto mt-4 flex w-72 flex-col gap-1.5 text-sm tabular">
          <div className="flex justify-between"><dt>סכום ביניים</dt><dd>{formatILS(q.subtotal)}</dd></div>
          {q.discountTotal > 0 && <div className="flex justify-between"><dt>הנחה</dt><dd>-{formatILS(q.discountTotal)}</dd></div>}
          {q.shipping > 0 && <div className="flex justify-between"><dt>משלוח</dt><dd>{formatILS(q.shipping)}</dd></div>}
          <div className="flex justify-between"><dt>מע״מ ({Math.round(q.vatRate * 100)}%)</dt><dd>{formatILS(q.vatAmount)}</dd></div>
          <div className="mt-1 flex justify-between border-t-2 border-ink pt-2 text-lg font-bold"><dt>סה״כ לתשלום</dt><dd>{formatILS(q.total)}</dd></div>
        </dl>
        {q.notes && <p className="mt-6 border-t border-line pt-3 text-sm whitespace-pre-wrap"><strong>הערות: </strong>{q.notes}</p>}
        {q.terms && <p className="mt-3 text-xs whitespace-pre-wrap text-muted">{q.terms}</p>}
      </Card>
    </div>
  );
}

export function QuotesCard({ quotes, orgId }: { quotes: { id: string; number: string; issueDate: string; status: QuoteStatus; total: number; viewedAt: string | null }[]; orgId: string }) {
  void orgId;
  return (
    <Card>
      <CardHeader title="הצעות מחיר" icon={<FileText />} count={quotes.length} />
      {!quotes.length ? (
        <EmptyState title="אין הצעות מחיר" />
      ) : (
        <ul className="divide-y divide-line/70">
          {quotes.map((q) => (
            <li key={q.id}>
              <Link to={`/quotes/${q.id}`} className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-sunken/60">
                <span className="ltr font-semibold">{q.number}</span>
                <span className="text-muted">{formatDate(q.issueDate)}</span>
                <Badge variant={QUOTE_STATUS_VARIANT[q.status]}>{QUOTE_STATUS_LABELS[q.status]}</Badge>
                <span className="ms-auto font-semibold tabular">{formatILS(q.total)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
